import { useTranslation } from "react-i18next"
import { Outlet, useLocation, useNavigate, useParams } from "react-router"
import { useCallback, useMemo } from "react"
import {
  useGetDeatiledSingleFinancialModelQuery,
  // useGetSingleFinancialModelSettingsV2Query,
} from "@/feature/financial-model/api"
import { useGetSelectedDepartmentsSuspenseQuery } from "@/feature/financial-model/ecommerce/personnel/api"
import { ModuleNavigation } from "@/components/shared/ModuleNavigation"
import { cn } from "@/lib/utils"
import { ViewSwitcher } from "@/components/shared/views-buttons"
import { usePersonnelStore } from "@/feature/financial-model/ecommerce/personnel/store/personnel"
import { EcommerceCommonHeader } from "@/components/shared/headers"
import { ScrollableRouteContainer } from "@/components/shared/Container"
import { ExportModuleDialog } from "@/feature/export/ecommerce/components/dowload"
import { PageBody, PageHeader } from "@/components/shared/page-sections.tsx"

type TabValue = "set-up" | "historical-data" | "forecast-data" | "total"

export const PersonnelHomeRoute = () => {
  const { t } = useTranslation(`personnel`)
  const navigate = useNavigate()
  const { id: financialModelId } = useParams()
  const location = useLocation()
  const isTotalsPage = location.pathname.includes(`total`)

  const handleTabsChange = useCallback(
    (value: TabValue) => {
      const tabNavigationMap: Record<TabValue, string> = {
        "set-up": `/dashboard/financial-model/${financialModelId}/ecommerce/personnel`,
        "historical-data": `/dashboard/financial-model/${financialModelId}/ecommerce/personnel/historical-data`,
        "forecast-data": `/dashboard/financial-model/${financialModelId}/ecommerce/personnel/forecast-data`,
        total: `/dashboard/financial-model/${financialModelId}/ecommerce/personnel/total`,
      }
      navigate(tabNavigationMap[value])
    },
    [financialModelId, navigate]
  )

  const { financialModel } = useGetDeatiledSingleFinancialModelQuery(
    financialModelId!
  )

  const { data: departments } = useGetSelectedDepartmentsSuspenseQuery(
    financialModelId!
  )

  const isSetupCompleted = useMemo(
    () =>
      departments.length > 0 &&
      departments?.every((department) => department.modeling_approach),
    [departments]
  )

  const view = usePersonnelStore((state) => state.view)
  const handleViewChange = usePersonnelStore((state) => state.setView)

  return (
    <ScrollableRouteContainer className={`p-0`}>
      <PageHeader className={`pb-0`}>
        <EcommerceCommonHeader
          linkText={t(`personnel.home.back`)}
          path={`/dashboard/financial-model/${financialModelId}/ecommerce`}
        />
        <div
          className={cn(`mt-6`, {
            [`flex justify-between items-center`]: isTotalsPage,
          })}
        >
          <div>
            <h1 className="page-title">{t(`personnel.home.title`)}</h1>
            <p className="page-subtitle">{financialModel?.data?.name}</p>
          </div>
          {isTotalsPage && (
            <div className="flex items-center gap-2">
              <ViewSwitcher view={view} onViewChange={handleViewChange} />
              <ExportModuleDialog
                financialModelId={financialModelId!}
                options={{ module: `personnel`, view }}
              />
            </div>
          )}
        </div>
      </PageHeader>

      <ModuleNavigation
        isSetupCompleted={isSetupCompleted}
        handleTabsChange={handleTabsChange}
      >
        <PageBody className="flex-1 w-full pb-28">
          <Outlet />
        </PageBody>
      </ModuleNavigation>
    </ScrollableRouteContainer>
  )
}
